import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Inbox, CalendarDays, MailWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/admin/PageHeader";
import { Toolbar, Segmented } from "@/components/admin/Toolbar";
import { DataTable, Cell2 } from "@/components/admin/DataTable";
import { StatusPill } from "@/components/admin/StatusPill";
import { BookingDetailSheet } from "@/components/admin/BookingDetailSheet";
import { adminBookings } from "@/lib/api";
import { fmtRange, fmtTime, fmtDate } from "@/lib/format";

const TABS = [["ny", "Nye"], ["godkjend", "Godkjende"], ["avvist", "Avviste"], ["alle", "Alle"]];

/** A quiet warning in the list when any e-mail about the request failed or has an unknown outcome – details in the sheet. */
const MailFlag = ({ emails, id }) => {
  const bad = Object.values(emails || {}).filter((e) => e?.state === "failed" || e?.state === "unknown" || e?.state === "blocked");
  if (!bad.length) return null;
  const label = bad.some((e) => e.state === "unknown") ? "E-post med uvisst utfall" : bad.some((e) => e.state === "blocked") ? "E-post stoppa" : "E-post feila";
  return <MailWarning className="h-4 w-4 text-status-warning" aria-label={label} data-testid={`booking-mail-flag-${id}`} />;
};

export default function Bookings() {
  const location = useLocation();
  const [tab, setTab] = useState("ny");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  // One list for all statuses → client-side tab + search (same API, counts for free).
  const { data: all, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-bookings"], queryFn: () => adminBookings() });

  useEffect(() => { if (location.state?.open) { setSelectedId(location.state.open); setTab("alle"); } }, [location.state]);

  const counts = useMemo(() => (all || []).reduce((acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }), { alle: all?.length || 0 }), [all]);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (all || [])
      .filter((b) => tab === "alle" || b.status === tab)
      .filter((b) => !needle || [b.name, b.organization, b.email, b.event_type, b.event_title, ...(b.room_names || [])].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [all, tab, q]);
  const conflicts = useMemo(() => (all || []).filter((b) => b.status === "ny" && b.has_conflict).length, [all]);

  const columns = [
    { key: "name", header: "Innsendar", primary: true, cell: (b) => <Cell2 title={<span className="font-medium">{b.name}</span>} sub={b.organization || b.email} /> },
    { key: "date", header: "Dato", meta: true, cell: (b) => <Cell2 title={<span className="whitespace-nowrap tabular">{fmtRange(b.start_date, b.end_date)}</span>} sub={fmtTime(b.start_time, b.end_time) || "Heile dagen"} /> },
    { key: "rooms", header: "Rom", meta: true, cell: (b) => <span className="text-sm">{b.room_names?.join(", ")}</span> },
    { key: "type", header: "Type", hide: "lg", meta: true, cell: (b) => <span className="text-sm">{b.event_title || b.event_type}{b.guests ? <span className="text-muted-foreground"> · {b.guests} pers.</span> : null}</span> },
    { key: "received", header: "Motteke", hide: "xl", cell: (b) => <span className="text-muted-foreground tabular">{fmtDate(b.created_at?.slice(0, 10), "d. MMM yyyy")}</span> },
    { key: "status", header: "Status", status: true, cell: (b) => <span className="inline-flex flex-wrap items-center gap-1.5"><StatusPill status={b.status} />{b.has_conflict && b.status === "ny" && <AlertTriangle className="h-4 w-4 text-status-warning" aria-label="Mogleg kollisjon" />}{b.source === "assistant" && <StatusPill status="husvert" />}<MailFlag emails={b.emails} id={b.id} /></span> },
  ];

  return (
    <div>
      <PageHeader
        title="Bookingførespurnader"
        description="Godkjenn eller avvis førespurnader. Godkjende bookingar vert automatisk lagde i kalenderen."
        status={conflicts > 0 && <StatusPill status="konflikt" label={`${conflicts} med mogleg kollisjon`} data-testid="bookings-conflict-count" />}
        secondary={<Button asChild variant="outline" className="h-9 bg-card" data-testid="bookings-open-calendar"><Link to="/admin/kalender?ventande=1"><CalendarDays className="h-4 w-4" /> Sjå i kalenderen</Link></Button>}
      />

      <Toolbar search={q} onSearch={setQ} searchPlaceholder="Søk på namn, lag, e-post, rom…" count={list.length} countLabel={list.length === 1 ? "førespurnad" : "førespurnader"} testId="bookings-toolbar">
        <Segmented value={tab} onChange={setTab} items={TABS.map(([v, l]) => ({ value: v, label: l, count: counts[v] ?? 0 }))} testId="bookings-tab" />
      </Toolbar>

      <DataTable
        testId="admin-bookings-table"
        columns={columns}
        rows={list}
        loading={isLoading}
        error={isError ? error : null}
        onRetry={refetch}
        onRowClick={(b) => setSelectedId(b.id)}
        rowTestId={(b) => `booking-row-${b.id}`}
        caption="Bookingførespurnader"
        empty={{ icon: Inbox, title: tab === "ny" ? "Ingen nye førespurnader" : "Ingen førespurnader her", text: q ? "Ingen treff på søket. Prøv eit anna ord eller tøm søkefeltet." : tab === "ny" ? "Alle nye førespurnader er handsama." : "Ingenting å vise i denne fana.", action: q ? <Button variant="outline" size="sm" className="h-9 bg-card" onClick={() => setQ("")}>Tøm søk</Button> : null }}
      />

      <BookingDetailSheet id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
